import type { IconName, NavGroup } from "@nabble/ui";
import { ADMINISTRATION_IDS, type AppModule } from "./appModules";

/** A place the user can go: Home, each section of each module, and My account. */
export interface NavTarget {
  id: string;
  label: string;
  icon: IconName;
  path: string;
  /** Label of the module the section belongs to; null for the suite's own pages. */
  module: string | null;
}

const HOME_PATH = "/";

/** Where a module opens: its first section. */
export function moduleLanding(module: AppModule): string {
  return module.sections[0]?.path ?? HOME_PATH;
}

export function navTargets(appModules: AppModule[]): NavTarget[] {
  return [
    { id: "overview", label: "Home", icon: "home", path: HOME_PATH, module: null },
    ...appModules.flatMap((m) => m.sections.map((s) => ({ id: s.id, label: s.label, icon: s.icon, path: s.path, module: m.label }))),
    { id: "account", label: "My account", icon: "user", path: "/account", module: null },
  ];
}

function moduleItem(module: AppModule): { id: string; label: string; icon: IconName; href: string } {
  return { id: module.id, label: module.label, icon: module.icon, href: moduleLanding(module) };
}

function navGroup(id: string, label: string, modules: AppModule[]): NavGroup[] {
  return modules.length > 0 ? [{ id, label, items: modules.map(moduleItem) }] : [];
}

/** The sidebar: Home, then the apps, then the administration (Organization, Admin Center). */
export function sidebarNav(appModules: AppModule[]): NavGroup[] {
  return [
    { id: "home", items: [{ id: "overview", label: "Home", icon: "home", href: HOME_PATH }] },
    ...navGroup("apps", "Apps", appModules.filter((m) => !ADMINISTRATION_IDS.has(m.id))),
    ...navGroup("admin", "Administration", appModules.filter((m) => ADMINISTRATION_IDS.has(m.id))),
  ];
}

function contains(target: NavTarget, pathname: string): boolean {
  if (target.path === HOME_PATH) return pathname === HOME_PATH;
  return pathname === target.path || pathname.startsWith(`${target.path}/`);
}

/** The most specific target the current path is in, or null. */
export function currentTarget(targets: NavTarget[], pathname: string): NavTarget | null {
  return targets.filter((t) => contains(t, pathname)).sort((a, b) => b.path.length - a.path.length)[0] ?? null;
}

/** True on a section's own page, not on a detail page below it. */
export function isSectionPage(module: AppModule, pathname: string): boolean {
  return module.sections.some((section) => section.path === pathname.replace(/\/+$/, ""));
}
